import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Toggle } from './ui/Toggle';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/api';

interface Comment {
  id: number;
  cod_comentario: string;
  comentario: string;
  familia: string | null;
  articulo: string | null;
  is_active?: number;
}

interface SmartBannerItemEditorProps {
  items: SmartBannerItem[];
  onChange: (items: SmartBannerItem[]) => void;
  availableReferences: ProductReference[];
  availableComments?: Comment[];
  loading?: boolean;
}

interface SmartBannerItem {
  id?: number;
  item_type: 'product' | 'custom' | 'extra';
  product_id?: number | null;
  comment_id?: number | null;
  custom_name?: string | null;
  custom_price?: number | null;
  custom_image_path?: string | null;
  show_image: number;
  show_price: number;
  display_order: number;
  group_name?: string | null;
}

interface ProductReference {
  reference_code: string;
  base_name: string;
  family?: string | null;
  variants: Array<{
    product_id: number;
    name: string;
    price: number;
    image_url?: string | null;
  }>;
}

export const SmartBannerItemEditor: React.FC<SmartBannerItemEditorProps> = ({
  items,
  onChange,
  availableReferences,
  availableComments = [],
  loading = false
}) => {
  const { token } = useAuth();
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemType, setNewItemType] = useState<'product' | 'custom' | 'extra'>('custom');
  const [selectedProductRef, setSelectedProductRef] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<string>('');
  const [selectedCommentId, setSelectedCommentId] = useState<string>('');
  const [newCustomName, setNewCustomName] = useState('');
  const [newCustomPrice, setNewCustomPrice] = useState<number | ''>('');
  const [selectedFamily, setSelectedFamily] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState<number | null>(null);

  // Obtener familias únicas de productos
  const families = Array.from(
    new Set(
      availableReferences
        .map(ref => ref.family)
        .filter((f): f is string => f !== null && f !== undefined && f !== '')
    )
  ).sort();

  // Obtener productos de una familia
  const getProductsByFamily = (family: string) => {
    return availableReferences.filter(ref => ref.family === family);
  };

  // Obtener variantes de un producto
  const getVariants = (referenceCode: string) => {
    const ref = availableReferences.find(r => r.reference_code === referenceCode);
    return ref?.variants || [];
  };

  const handleAddItem = () => {
    if (newItemType === 'product') {
      if (!selectedProductRef) {
        toast.error('Debes seleccionar un producto');
        return;
      }

      const variantId = selectedVariant ? parseInt(selectedVariant, 10) : null;
      const ref = availableReferences.find(r => r.reference_code === selectedProductRef);
      
      if (!ref || ref.variants.length === 0) {
        toast.error('El producto seleccionado no tiene variantes disponibles');
        return;
      }

      const variant = variantId 
        ? ref.variants.find(v => v.product_id === variantId)
        : ref.variants[0];

      if (!variant) {
        toast.error('Variante no encontrada');
        return;
      }

      const newItem: SmartBannerItem = {
        item_type: 'product',
        product_id: variant.product_id,
        show_image: 1,
        show_price: 1,
        display_order: items.length,
        group_name: ref.family || null
      };

      onChange([...items, newItem]);
      
      // Reset form
      setSelectedProductRef('');
      setSelectedVariant('');
      setShowAddItem(false);
    } else if (newItemType === 'extra') {
      if (!selectedCommentId) {
        toast.error('Debes seleccionar un extra');
        return;
      }

      const comment = availableComments.find(c => c.id.toString() === selectedCommentId);
      if (!comment) {
        toast.error('Extra no encontrado');
        return;
      }

      const newItem: SmartBannerItem = {
        item_type: 'extra',
        comment_id: comment.id,
        show_image: 0,
        show_price: 0,
        display_order: items.length,
        group_name: comment.familia || null
      };

      onChange([...items, newItem]);
      
      // Reset form
      setSelectedCommentId('');
      setShowAddItem(false);
    } else {
      if (!newCustomName.trim()) {
        toast.error('El nombre del item personalizado es requerido');
        return;
      }

      const newItem: SmartBannerItem = {
        item_type: 'custom',
        custom_name: newCustomName.trim(),
        custom_price: newCustomPrice !== '' ? Number(newCustomPrice) : null,
        show_image: 0,
        show_price: newCustomPrice !== '' ? 1 : 0,
        display_order: items.length,
        group_name: null
      };

      onChange([...items, newItem]);
      
      // Reset form
      setNewCustomName('');
      setNewCustomPrice('');
      setShowAddItem(false);
    }
  };

  const handleAddFamily = () => {
    if (!selectedFamily) {
      toast.error('Debes seleccionar una familia');
      return;
    }

    const familyProducts = getProductsByFamily(selectedFamily);
    if (familyProducts.length === 0) {
      toast.error('La familia seleccionada no tiene productos');
      return;
    }

    const newItems: SmartBannerItem[] = familyProducts.flatMap(ref => {
      if (ref.variants.length === 0) return [];
      
      return ref.variants.map((variant, index) => ({
        item_type: 'product' as const,
        product_id: variant.product_id,
        show_image: 1,
        show_price: 1,
        display_order: items.length + index,
        group_name: ref.family || null
      }));
    });

    onChange([...items, ...newItems]);
    setSelectedFamily('');
    toast.success(`${newItems.length} productos agregados de la familia "${selectedFamily}"`);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    // Reordenar display_order
    const reordered = newItems.map((item, i) => ({
      ...item,
      display_order: i
    }));
    onChange(reordered);
  };

  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newItems.length) {
      return;
    }

    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    
    // Actualizar display_order
    const reordered = newItems.map((item, i) => ({
      ...item,
      display_order: i
    }));
    
    onChange(reordered);
  };

  const handleUpdateItem = (index: number, updates: Partial<SmartBannerItem>) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onChange(newItems);
  };

  const uploadItemImage = async (file: File, itemIndex: number): Promise<string | null> => {
    if (!file || !token) return null;

    setUploadingImage(itemIndex);
    try {
      const formData = new FormData();
      formData.append('item_image', file);
      formData.append('image_type', 'item');

      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      const url = `${API_BASE_URL}.php?route=smart-banners/upload-image`;
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        const text = await res.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch (e) {
          throw new Error(`Error del servidor (${res.status}): ${text.substring(0, 200)}`);
        }
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al subir imagen';
        throw new Error(errorMessage);
      }

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch (e) {
        throw new Error('Respuesta inválida del servidor');
      }
      
      const imagePath = json.data?.image_path || json.image_path || null;
      return imagePath;
    } catch (err: any) {
      console.error('Error subiendo imagen:', err);
      toast.error(err.message || 'Error al subir imagen');
      throw err;
    } finally {
      setUploadingImage(null);
    }
  };

  const handleItemImageChange = async (e: React.ChangeEvent<HTMLInputElement>, itemIndex: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamaño (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('El archivo es demasiado grande. Tamaño máximo: 5MB');
      return;
    }

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Solo se permiten: JPEG, PNG, GIF, WEBP');
      return;
    }

    try {
      const imagePath = await uploadItemImage(file, itemIndex);
      if (imagePath) {
        handleUpdateItem(itemIndex, { custom_image_path: imagePath, show_image: 1 });
        toast.success('Imagen subida exitosamente');
      }
    } catch (err) {
      // El error ya se muestra en uploadItemImage
    }
    
    // Limpiar el input
    e.target.value = '';
  };

  const getItemDisplayName = (item: SmartBannerItem): string => {
    if (item.item_type === 'product') {
      const ref = availableReferences.find(r => 
        r.variants.some(v => v.product_id === item.product_id)
      );
      if (ref) {
        const variant = ref.variants.find(v => v.product_id === item.product_id);
        return variant?.name || ref.base_name || 'Producto';
      }
      return 'Producto';
    } else if (item.item_type === 'extra') {
      const comment = availableComments.find(c => c.id === item.comment_id);
      return comment?.comentario || 'Extra';
    } else {
      return item.custom_name || 'Item personalizado';
    }
  };

  const getItemPrice = (item: SmartBannerItem): number | null => {
    if (item.item_type === 'product') {
      const ref = availableReferences.find(r => 
        r.variants.some(v => v.product_id === item.product_id)
      );
      if (ref) {
        const variant = ref.variants.find(v => v.product_id === item.product_id);
        const price = variant?.price;
        // Asegurar que sea un número
        if (price !== null && price !== undefined) {
          const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
          return isNaN(numPrice) ? null : numPrice;
        }
        return null;
      }
      return null;
    } else {
      const price = item.custom_price;
      // Asegurar que sea un número
      if (price !== null && price !== undefined) {
        const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
        return isNaN(numPrice) ? null : numPrice;
      }
      return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Agregar por familia */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Select
                label="Agregar Familia Completa"
                value={selectedFamily}
                onChange={(e) => setSelectedFamily(e.target.value)}
                options={[
                  { value: '', label: '-- Selecciona una familia --' },
                  ...families.map(f => ({ value: f, label: f }))
                ]}
                disabled={loading || families.length === 0}
                helperText={families.length === 0 ? 'No hay familias disponibles' : 'Agrega todos los productos de una familia'}
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddFamily}
                disabled={!selectedFamily || loading}
              >
                Agregar Familia
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Formulario para agregar item */}
      {showAddItem ? (
        <Card>
          <CardContent className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Agregar Item</h4>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddItem(false);
                    setNewItemType('custom');
                    setSelectedProductRef('');
                    setSelectedVariant('');
                    setSelectedCommentId('');
                    setNewCustomName('');
                    setNewCustomPrice('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <Select
                label="Tipo de Item"
                value={newItemType}
                onChange={(e) => {
                  setNewItemType(e.target.value as 'product' | 'custom' | 'extra');
                  setSelectedProductRef('');
                  setSelectedVariant('');
                  setSelectedCommentId('');
                  setNewCustomName('');
                  setNewCustomPrice('');
                }}
                options={[
                  { value: 'product', label: 'Producto de la BD' },
                  { value: 'extra', label: 'Extra (Comentario)' },
                  { value: 'custom', label: 'Item Personalizado' }
                ]}
              />

              {newItemType === 'product' ? (
                <>
                  <Select
                    label="Producto"
                    value={selectedProductRef}
                    onChange={(e) => {
                      setSelectedProductRef(e.target.value);
                      setSelectedVariant('');
                    }}
                    options={[
                      { value: '', label: '-- Selecciona un producto --' },
                      ...availableReferences.map(ref => ({
                        value: ref.reference_code,
                        label: `${ref.base_name}${ref.family ? ` (${ref.family})` : ''}`
                      }))
                    ]}
                    disabled={loading || availableReferences.length === 0}
                  />

                  {selectedProductRef && (
                    <Select
                      label="Variante"
                      value={selectedVariant}
                      onChange={(e) => setSelectedVariant(e.target.value)}
                      options={[
                        { value: '', label: '-- Selecciona una variante --' },
                        ...getVariants(selectedProductRef).map(v => {
                          const price = typeof v.price === 'number' && !isNaN(v.price) 
                            ? v.price 
                            : (typeof v.price === 'string' ? parseFloat(v.price) : 0);
                          const safePrice = isNaN(price) ? 0 : price;
                          return {
                            value: v.product_id.toString(),
                            label: `${v.name} - $${Math.round(safePrice)}`
                          };
                        })
                      ]}
                    />
                  )}
                </>
              ) : newItemType === 'extra' ? (
                <>
                  <Select
                    label="Extra (Comentario)"
                    value={selectedCommentId}
                    onChange={(e) => setSelectedCommentId(e.target.value)}
                    options={[
                      { value: '', label: '-- Selecciona un extra --' },
                      ...availableComments.map(comment => ({
                        value: comment.id.toString(),
                        label: `${comment.comentario}${comment.cod_comentario ? ` (${comment.cod_comentario})` : ''}${comment.familia ? ` - ${comment.familia}` : ''}`
                      }))
                    ]}
                    disabled={loading || availableComments.length === 0}
                    helperText={availableComments.length === 0 ? 'No hay extras disponibles. Sincroniza desde Hioffice primero.' : 'Selecciona un comentario de la lista de extras'}
                  />
                </>
              ) : (
                <>
                  <Input
                    label="Nombre del Item"
                    value={newCustomName}
                    onChange={(e) => setNewCustomName(e.target.value)}
                    placeholder="Ej: Ingrediente especial"
                    required
                  />

                  <Input
                    label="Precio (Opcional)"
                    type="number"
                    step="0.01"
                    value={newCustomPrice}
                    onChange={(e) => setNewCustomPrice(e.target.value === '' ? '' : parseFloat(e.target.value))}
                    placeholder="0.00"
                  />
                </>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleAddItem}
                  className="flex-1"
                >
                  Agregar Item
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowAddItem(false);
                    setNewItemType('custom');
                    setSelectedProductRef('');
                    setSelectedVariant('');
                    setSelectedCommentId('');
                    setNewCustomName('');
                    setNewCustomPrice('');
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setShowAddItem(true)}
          className="w-full"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Agregar Item
        </Button>
      )}

      {/* Lista de items */}
      {items.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            <p>No hay items agregados. Haz clic en "Agregar Item" para comenzar.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={item.item_type === 'product' ? 'success' : item.item_type === 'extra' ? 'info' : 'neutral'}>
                          {item.item_type === 'product' ? 'Producto' : item.item_type === 'extra' ? 'Extra' : 'Personalizado'}
                        </Badge>
                        {item.group_name && (
                          <Badge variant="neutral" className="text-xs">
                            {item.group_name}
                          </Badge>
                        )}
                      </div>
                      <h5 className="font-semibold text-gray-900">{getItemDisplayName(item)}</h5>
                      {(() => {
                        const price = getItemPrice(item);
                        return price !== null && typeof price === 'number' && !isNaN(price) ? (
                          <p className="text-sm text-gray-600">${Math.round(price)}</p>
                        ) : null;
                      })()}
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleMoveItem(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        title="Mover arriba"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveItem(index, 'down')}
                        disabled={index === items.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        title="Mover abajo"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="p-1 text-red-400 hover:text-red-600"
                        title="Eliminar"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                    <Toggle
                      label="Mostrar Imagen"
                      checked={item.show_image === 1}
                      onChange={(checked) => handleUpdateItem(index, { show_image: checked ? 1 : 0 })}
                    />
                    <Toggle
                      label="Mostrar Precio"
                      checked={item.show_price === 1}
                      onChange={(checked) => handleUpdateItem(index, { show_price: checked ? 1 : 0 })}
                    />
                  </div>

                  {item.item_type === 'custom' && (
                    <div className="pt-2 border-t border-gray-200 space-y-2">
                      <Input
                        label="Nombre"
                        value={item.custom_name || ''}
                        onChange={(e) => handleUpdateItem(index, { custom_name: e.target.value })}
                        placeholder="Nombre del item"
                      />
                      <Input
                        label="Precio"
                        type="number"
                        step="0.01"
                        value={item.custom_price || ''}
                        onChange={(e) => handleUpdateItem(index, { 
                          custom_price: e.target.value === '' ? null : parseFloat(e.target.value),
                          show_price: e.target.value !== '' ? 1 : 0
                        })}
                        placeholder="0.00"
                      />
                      
                      {/* Subida de imagen para items personalizados */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Imagen del Item
                        </label>
                        {item.custom_image_path ? (
                          <div className="space-y-2">
                            <div className="relative w-full h-32 bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                              <img
                                src={getImageUrl(item.custom_image_path)}
                                alt={item.custom_name || 'Item image'}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                onChange={(e) => handleItemImageChange(e, index)}
                                className="hidden"
                                disabled={uploadingImage === index}
                                id={`item-image-input-${index}`}
                              />
                              <Button
                                type="button"
                                variant="secondary"
                                className="flex-1"
                                disabled={uploadingImage === index}
                                onClick={() => {
                                  const input = document.getElementById(`item-image-input-${index}`) as HTMLInputElement;
                                  input?.click();
                                }}
                              >
                                {uploadingImage === index ? 'Subiendo...' : 'Cambiar Imagen'}
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => handleUpdateItem(index, { custom_image_path: null, show_image: 0 })}
                                disabled={uploadingImage === index}
                              >
                                Eliminar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="block">
                            <input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                              onChange={(e) => handleItemImageChange(e, index)}
                              className="hidden"
                              disabled={uploadingImage === index}
                              id={`item-image-input-${index}`}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              className="w-full"
                              disabled={uploadingImage === index}
                              onClick={() => {
                                const input = document.getElementById(`item-image-input-${index}`) as HTMLInputElement;
                                input?.click();
                              }}
                            >
                              {uploadingImage === index ? 'Subiendo...' : 'Subir Imagen'}
                            </Button>
                          </div>
                        )}
                        <Input
                          label="Orden de Visualización (display_order)"
                          type="number"
                          min="0"
                          value={item.display_order}
                          onChange={(e) => handleUpdateItem(index, { display_order: parseInt(e.target.value, 10) || 0 })}
                          placeholder="0"
                          helperText="Para el banner tipo 'Postres': 1 = AGUA, 2 = REGULAR (chips), 3 = GALLETA"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

